/**
 * Auth token storage key — intentionally the SAME slot the @mtm/auth SDK uses
 * (TokenManager's APP_TOKEN_STORAGE_KEY), so the webapp and core-fe share one token.
 */
export const APP_TOKEN_STORAGE_KEY = 'app_token';

/**
 * Deep link captured by `authGuestGuard` before it bounces an anonymous visitor to
 * `/entry/login?redirect_uri=...`. Kept in sessionStorage because the query param does not
 * survive the SSO hops (`/entry/auth/process`, `/entry/tenant-select`) and because
 * NoAuthGuard's stale-session cleanup runs `localStorage.clear()` on the way into login.
 * Consumed (and cleared) once by `MtmAuthGlueService.redirectAfterAuthentication()`.
 */
export const REDIRECT_URI_STORAGE_KEY = 'auth_redirect_uri';

/** Language chosen by the person using this browser — survives logout so the login page keeps it. */
export const AUTH_LANGUAGE_STORAGE_KEY = 'auth_user_language';

/**
 * Brand each user was last in, as `{ [username]: brandId }`. Written on every brand change and
 * read when resolving the brand to open after login, so a user returns to where they left off.
 * Keyed by username because one browser can be shared: without the key, the previous person's
 * brand would decide where the next one lands.
 */
export const LAST_VISITED_BRAND_STORAGE_KEY = 'last_visited_brand';

/**
 * Share link each guest last opened, as `{ [email]: surfaceUuid }`. A guest's app token cannot be
 * renewed (the IAM SDK renews for a tenant, and a guest has none), so once it expires the session
 * restore lands on an empty tenant picker. This is what lets `MtmAuthGlueService` send the guest
 * back to their link instead, which mints a fresh guest token from the still-valid IAM cookie.
 * Keyed by email for the same reason as {@link LAST_VISITED_BRAND_STORAGE_KEY}: a shared browser
 * must not send the next person to the previous guest's link.
 */
export const LAST_SHARE_LINK_STORAGE_KEY = 'last_share_link';

/**
 * Where an impersonation ("switch user") was started from. Only set when it came from the
 * platform admin console, which runs on another host: `admin_token` may then be empty (the admin
 * had no session in this app to stash), so this is what keeps "Logout on behalf" visible, and it
 * is what makes that button end the impersonation here instead of in this app's own admin pages.
 */
export const IMPERSONATION_ORIGIN_STORAGE_KEY = 'impersonation_origin';

/** Value of {@link IMPERSONATION_ORIGIN_STORAGE_KEY} for a switch handed over by the console. */
export const IMPERSONATION_ORIGIN_ADMIN_CONSOLE = 'admin-console';
